import { capEditPrimaryApi } from '@/utils/api';
import type { EntityMember as Member } from '@/utils/api/api';
import type {
  EntityAdministrator as Administrator,
  EntityCapabilities
} from '@/utils/api/maintenance/api';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * ログイン情報のStore
 */
export const useLoginStore = defineStore(
  'login',
  () => {
    /** 【専門組織】ログインユーザー */
    const member = ref<Member | undefined>(undefined);

    /** 【管理者】ログインユーザー */
    const administrator = ref<Administrator | undefined>(undefined);

    /** 【隊員】隊員番号 */
    const crewNo = ref<string | undefined>(undefined);

    /** ユーザー名 */
    const memberName = ref<string>('');

    /** アクセストークン */
    const token = ref<string>('');

    const setToken = (value: string): void => {
      token.value = value;
    };

    /** トークン有効期限切れ */
    const isTokenExpired = ref<boolean>(false);

    const setTokenExpired = (value: boolean): void => {
      isTokenExpired.value = value;
    };

    /** 【二次専門組織】招待状ID */
    const invitationId = ref<string | undefined>(undefined);

    const setInvitationId = (value: string): void => {
      invitationId.value = value;
    };

    /**
     * 【専門組織】サービスID
     * @returns サービスID
     */
    const getServiceId = (): number | undefined => {
      if (member?.value?.services?.length) {
        return member.value.services[0].serviceId;
      }
      return undefined;
    };

    /**
     * 【専門組織】ログイン
     * @param value
     */
    const login = (value: Member): void => {
      member.value = value;
      memberName.value = value.member?.name ?? '';
      crewNo.value = undefined;
    };

    /**
     * 【管理者】ログイン
     * @param value
     */
    const loginAdmin = (value: Administrator): void => {
      administrator.value = value;
      memberName.value = value.name ?? '';
      crewNo.value = undefined;
    };

    /**
     * ログアウト
     */
    const logout = (): void => {
      member.value = undefined;
      memberName.value = '';
      crewNo.value = undefined;
      administrator.value = undefined;
      token.value = '';
      isTokenExpired.value = false;
      invitationId.value = undefined;
    };

    /**
     * 管理者かどうかの判定
     */
    const isAdmin = computed(() => {
      return administrator.value != undefined;
    });

    const isCrew = computed(() => {
      return crewNo.value != undefined;
    });

    /**
     * 【管理者】操作権限
     */
    const adminCapabilities = computed<EntityCapabilities | undefined>(() => {
      return administrator.value?.capabilities;
    });

    return {
      member,
      administrator,
      crewNo,
      memberName,
      getServiceId,
      login,
      loginAdmin,
      logout,
      token,
      setToken,
      isTokenExpired,
      setTokenExpired,
      invitationId,
      setInvitationId,
      isAdmin,
      isCrew,
      adminCapabilities
    };
  },
  { persist: { storage: sessionStorage } }
);
